/**
 * Beregner hvilke asset-paths som er i bruk på tvers av alle skjermer i en
 * organisasjon. Brukes til å avgjøre om et user-uploaded library-item kan
 * slettes trygt — vi vil ikke at brukeren sletter en fil mens den er
 * referert fra en aktiv slide eller en lagret slide-mal i biblioteket.
 *
 * Henter alle skjermer + per-skjerm-detaljer + library parallelt og samler:
 *   - `slide.media` fra hver slide i playlistene (aktiv bruk)
 *   - `assets[]` fra hvert library-item med `mediaType === 'slide'`
 *     (lagret slide-mal — kan brukes igjen senere så asseten må beholdes)
 *
 * Resultatet caches i 30 sek per orgId så multiple gallery-mounts ikke
 * trigger ny fetchstorm. Cache invalidates ved hver sletting via
 * `invalidateAssetUsage(orgId)`.
 */
import type { CustomSlide } from 'src/content/screens/slide-element';
import { isSameCustomSlide } from 'src/content/screens/slide-equality';
import { getScreensForOrganization, fetchScreen, fetchLibrary } from '../../store/signage';

const USAGE_TTL_MS = 30 * 1000;

interface UsageEntry {
  paths: Set<string>;
  fetchedAt: number;
}

interface SlideUsageEntry {
  ids: Set<string>;
  fetchedAt: number;
}

const cache = new Map<number, UsageEntry>();
const savedSlideCache = new Map<number, SlideUsageEntry>();

export async function getAssetUsage(organizationId: number): Promise<Set<string>> {
  if (!organizationId) return new Set();

  const cached = cache.get(organizationId);
  if (cached && Date.now() - cached.fetchedAt < USAGE_TTL_MS) {
    return cached.paths;
  }

  const paths = new Set<string>();
  try {
    const [screens, library] = await Promise.all([
      getScreensForOrganization(organizationId),
      fetchLibrary(organizationId).catch((err) => {
        console.warn('[asset-usage] fetchLibrary failed:', err);
        return [];
      }),
    ]);
    const details = await Promise.all(
      screens.map((s) =>
        fetchScreen(s.displayId, organizationId).catch((err) => {
          console.warn(`[asset-usage] fetchScreen failed for ${s.displayId}:`, err);
          return null;
        }),
      ),
    );
    for (const detail of details) {
      if (!detail) continue;
      for (const slide of detail.playlistItems) {
        const media = (slide as { media?: unknown }).media;
        if (typeof media === 'string' && media) paths.add(media);
      }
    }
    // Lagrede slide-maler i biblioteket "låser" sine assets fra sletting —
    // ellers kan brukeren slette en asset som er en del av en mal og ende
    // opp med broken bilde neste gang malen velges fra forslag.
    for (const item of library) {
      if (item.mediaType !== 'slide') continue;
      for (const a of item.assets ?? []) {
        if (a) paths.add(a);
      }
    }
  } catch (err) {
    console.warn('[asset-usage] failed to compute usage:', err);
    // Returner hva vi har; UI faller tilbake til "alle ser ut til å være i
    // bruk" → ingen X-knapper vises, som er trygt default.
  }

  cache.set(organizationId, { paths, fetchedAt: Date.now() });
  return paths;
}

export function invalidateAssetUsage(organizationId: number): void {
  cache.delete(organizationId);
}

/**
 * Beregner hvilke saved-slide library-item-ID-er som er i bruk på tvers av
 * alle skjermer. Brukes til å avgjøre om en lagret slide kan slettes — kun
 * slides som ikke er i aktiv bruk i noen playlist kan slettes.
 *
 * Sammenligner library-slides mot playlist-slides med `isSameCustomSlide()`
 * (innholdsbasert, ikke ID-basert, siden playlist-slides får ny UUID ved
 * `onSelectSuggestion` og library-ID-en ikke følger med).
 *
 * Resultatet caches med samme TTL som `getAssetUsage`. Cache invalideres via
 * `invalidateSavedSlideUsage(orgId)` etter sletting.
 */
export async function getSavedSlideUsage(organizationId: number): Promise<Set<string>> {
  if (!organizationId) return new Set();

  const cached = savedSlideCache.get(organizationId);
  if (cached && Date.now() - cached.fetchedAt < USAGE_TTL_MS) {
    return cached.ids;
  }

  const ids = new Set<string>();
  try {
    const [screens, library] = await Promise.all([
      getScreensForOrganization(organizationId),
      fetchLibrary(organizationId).catch((err) => {
        console.warn('[saved-slide-usage] fetchLibrary failed:', err);
        return [];
      }),
    ]);

    const details = await Promise.all(
      screens.map((s) =>
        fetchScreen(s.displayId, organizationId).catch((err) => {
          console.warn(`[saved-slide-usage] fetchScreen failed for ${s.displayId}:`, err);
          return null;
        }),
      ),
    );

    const playlistSlides = details
      .filter((d) => d !== null)
      .flatMap((d) => d!.playlistItems)
      .filter((s): s is CustomSlide => s.type === 'custom');

    for (const item of library) {
      if (item.mediaType !== 'slide' || !item.slide) continue;
      if (playlistSlides.some((ps) => isSameCustomSlide(item.slide as CustomSlide, ps))) {
        ids.add(item.id);
      }
    }
  } catch (err) {
    console.warn('[saved-slide-usage] failed to compute usage:', err);
  }

  savedSlideCache.set(organizationId, { ids, fetchedAt: Date.now() });
  return ids;
}

export function invalidateSavedSlideUsage(organizationId: number): void {
  savedSlideCache.delete(organizationId);
}
